import { useCallback, useMemo } from 'react';
import { NodeSelection } from 'prosemirror-state';
import { findParentNode, findParentNodeOfType } from 'prosemirror-utils';

import { REACT_COMPONENT_CLASSNAME } from '@lyearn/lygo-core';

import { blockMenuKey } from '../../plugins/BlockMenu/utils/utils';
import { PopperProps } from '../../types';
import {
  getBlockMenuBaseLeftOffset,
  getElementPos,
  insertNode,
  openAddMenu,
  selectionType,
  selectNode,
  setPluginMeta,
  setSelection,
} from '../../utils';
import { MenuType } from '../BlockMenu/types';
import { HEIGHTS, INITIAL_LEFT_OFFSET, INITIAL_TOP_OFFSET, MENU_BUTTON_HEIGHT } from './consts';
import { buttonClickProps } from './types';
import { dragCopyModifier, Dragging } from './utils';

export const usePopperOptions = (anchorEl: PopperProps['anchorEl']) => {
  return useMemo(() => {
    let topOffSet = INITIAL_TOP_OFFSET;
    if (anchorEl && HEIGHTS[(anchorEl as any).tagName]) {
      topOffSet = (HEIGHTS[(anchorEl as any).tagName] - MENU_BUTTON_HEIGHT) / 2;
    }
    return {
      modifiers: [
        {
          name: 'offset',
          options: {
            offset: [topOffSet, INITIAL_LEFT_OFFSET + getBlockMenuBaseLeftOffset(anchorEl)],
          },
        },
        {
          name: 'flip',
          enabled: false,
        },
      ],
    };
  }, [anchorEl]);
};

export const useBlockMenuButtonClick: buttonClickProps = (open, anchorEl, view) => {
  return useCallback(() => {
    if (open && anchorEl && view) {
      let blockMenuProps = {
        open: true,
        anchorEl: anchorEl,
        menuType: MenuType.GENERAL,
      };
      const anchorElement = anchorEl as HTMLElement;
      if (anchorElement && anchorElement.classList.contains(REACT_COMPONENT_CLASSNAME)) {
        anchorElement.classList.add('block-menu-selected');
      }
      selectNode(anchorEl, view);
      setPluginMeta(view, blockMenuKey, blockMenuProps);
    }
  }, [open, anchorEl, view]);
};

export const useBlockMenuButtonDragStart: buttonClickProps = (open, anchorEl, view) => {
  return useCallback(
    (event: any) => {
      if (open && anchorEl && view) {
        const elementPos = getElementPos(anchorEl, view);
        if (elementPos && elementPos.inside !== undefined) {
          view.dispatch(
            view.state.tr.setSelection(
              new NodeSelection(view.state.doc.resolve(elementPos.inside)),
            ),
          );

          // check if the selected element is a list item
          const isNodeViewListNode = findParentNodeOfType([
            view.state.schema.nodes.toggle_list,
            view.state.schema.nodes.check_list,
          ])(view.state.selection);
          if (isNodeViewListNode) {
            const currentPos = view.state.selection.$from.pos;
            setSelection(view, selectionType.NODE, currentPos - 1);
          }
          let slice = view.state.selection.content();
          // Drop is handled by the prosemirror.
          // ref: https://github.com/ProseMirror/prosemirror-view/blob/dfa84353afc7603a250501cbaaf2a684f6d6c2be/src/input.js#L559
          view.dragging = new Dragging(slice, !event[dragCopyModifier]);
          event.dataTransfer.clearData();
          event.dataTransfer.setData('text/html', (anchorEl as any).innerHTML);
          event.dataTransfer.setDragImage(anchorEl, 10, 10);
        }
        let blockMenuProps = {
          open: false,
          anchorEl: null,
        };
        setPluginMeta(view, blockMenuKey, blockMenuProps);
      }
    },
    [open, anchorEl, view],
  );
};

export const useAddMenuButtonClick: buttonClickProps = (open, anchorEl, view) => {
  return useCallback(() => {
    if (open && anchorEl && view) {
      selectNode(anchorEl, view);
      const parentTable = findParentNode((node) => view.state.schema.nodes.table === node.type)(
        view.state.selection,
      );
      // check if the selected element is a list item
      const nodeViewListNode = findParentNodeOfType([
        view.state.schema.nodes.toggle_list,
        view.state.schema.nodes.check_list,
      ])(view.state.selection);

      if (parentTable) {
        const insertParaPos = parentTable.pos + parentTable.node.nodeSize;
        insertNode(
          view,
          selectionType.NODE,
          view.state.schema.nodes.paragraph.create(),
          insertParaPos,
        );
        setSelection(view, selectionType.TEXT, insertParaPos + 1);
      } else if (nodeViewListNode) {
        const insertPos = nodeViewListNode.pos + nodeViewListNode.node.nodeSize;
        insertNode(view, selectionType.NODE, view.state.schema.nodes.paragraph.create(), insertPos);
        setSelection(view, selectionType.TEXT, insertPos + 1);
      } else if (
        (view.state.selection as any).node.isLeaf ||
        (view.state.selection as any).node.content.size ||
        (view.state.selection as any).node.type !== view.state.schema.nodes.paragraph
      ) {
        insertNode(
          view,
          selectionType.NODE,
          view.state.schema.nodes.paragraph.create(),
          view.state.selection.to,
        );
        setSelection(view, selectionType.TEXT, view.state.selection.to + 1);
      } else {
        // It will be empty text node so set the selection to the inner element.
        setSelection(view, selectionType.TEXT, view.state.selection.to - 1);
      }
      view.focus();
      openAddMenu(view, view.state.selection.to);
    }
  }, [open, anchorEl, view]);
};
